<script setup>
import { useLayout } from '@/layout/common/composables/layout';
import { cva } from 'class-variance-authority';
import { computed, inject, watch } from 'vue';

const props = defineProps({
    class: {
        type: String,
        default: ''
    },
    asChild: {
        type: Boolean,
        default: false
    },
    isActive: {
        type: Boolean,
        default: false
    },
    to: {
        type: String,
        default: ''
    },
    href: {
        type: String,
        default: ''
    },
    target: {
        type: String,
        default: '_self'
    },
    mode: {
        type: String,
        default: null
    },
    isFirst: {
        type: Boolean,
        default: false
    },
    tooltipValue: {
        type: String,
        default: ''
    }
});

const menuContext = inject('menuContext', null);

const handleClick = (event) => {
    if (menuContext?.collapsible && !props.to && !props.href) {
        event.preventDefault();
        menuContext.toggle();
    }
};

// Reveal the group holding the current page. A watch, not onMounted: in-app
// navigation flips isActive without remounting the button, so a mount-only
// check left the group shut around the page the user had just opened.
watch(
    () => props.isActive,
    (isActive) => {
        if (isActive && menuContext?.collapsible) {
            menuContext.open();
        }
    },
    { immediate: true }
);

const { layoutConfig, calculatedMenuMode } = useLayout();

const calculatedVariant = computed(() => {
    if (props.mode) {
        return props.mode;
    }
    return calculatedMenuMode.value;
});

const sidebarMenuButtonVariants = cva(
    'group inline-flex w-full min-h-10 items-center gap-3 rounded-xl px-4 py-2 font-medium text-surface-600 dark:text-surface-100 data-[active=false]:hover:bg-primary-50 dark:data-[active=false]:hover:bg-surface-700 data-[active=true]:bg-primary-50 dark:data-[active=true]:bg-surface-800 data-[active=true]:text-primary-700 data-[active=true]:font-semibold dark:data-[active=true]:text-primary-200 transition-all duration-200 [&_span]:whitespace-nowrap [&_span]:truncate [&_i]:text-surface-500 dark:[&_i]:text-surface-100',
    {
        variants: {
            variant: {
                slim: 'w-16 h-auto !py-2 flex-col [&_span]:text-[11px] !px-1 !rounded-lg [&_span]:w-fit [&_span]:truncate [&_span]:max-w-full',
                compact: 'w-10 h-10 !p-[0.675rem] [&_span]:hidden',
                drawer: 'data-[state=collapsed]:!p-[0.675rem] data-[state=collapsed]:w-10 data-[state=expanded]:w-full min-h-10 data-[state=collapsed]:[&_span]:hidden',
                reveal: 'float-right flex-row-reverse justify-between min-h-10 data-[state=collapsed]:!pr-[0.675rem] !w-full',
                horizontal: '',
                static: ''
            }
        }
    }
);

const sidebarMenuButtonClass = computed(() => {
    return sidebarMenuButtonVariants({ variant: calculatedVariant.value, class: props.class });
});
</script>

<template>
    <component
        :is="props?.asChild ? 'div' : props?.href ? 'a' : props?.to ? 'router-link' : 'button'"
        :data-active="props?.isActive"
        :href="props?.href || props?.to"
        :target="props?.target"
        :to="props?.to"
        @click="handleClick"
        :data-variant="calculatedVariant"
        :data-state="layoutConfig.menuState"
        :class="[props.asChild ? props.class : [sidebarMenuButtonClass, calculatedVariant === 'reveal' && props.isFirst ? '!w-[15rem]' : '', menuContext?.isOpen.value ? 'bg-surface-200/75 dark:bg-surface-800/75' : '', props.class]]"

    >
        <slot />
        <span
            v-if="menuContext?.collapsible || calculatedVariant === 'horizontal'"
            class="group-data-[state=collapsed]:opacity-0 transition-opacity duration-300"
            :class="[
                'text-sm font-medium',
                calculatedVariant === 'slim' ? 'mt-1 text-[11px] leading-none' : '',
                calculatedVariant === 'reveal' ? 'flex-1 text-start' : '',
                calculatedVariant === 'horizontal' ? 'hidden lg:inline-block' : ''
            ]"
        >
            <i class="pi pi-chevron-down transition-transform !text-xs group-data-[popover-open=true]/sidebar-popover:rotate-180" :class="{ 'rotate-180': menuContext.isOpen.value }" />
        </span>
    </component>
</template>

<style scoped>
/* Active state styling using CSS variables */
[data-active="true"] {
    position: relative;
    background: var(--menu-item-active-bg) !important;
    transition: background var(--menu-item-transition-duration) ease-in-out;
}

[data-active="true"]::before {
    content: '';
    position: absolute;
    inset-block: .65rem;
    inset-inline-start: -.12rem;
    width: 3px;
    border-radius: 999px;
    background: linear-gradient(180deg, var(--sfh-teal, #0095a3), var(--sfh-gold, #fcbb4b));
    box-shadow: 0 2px 8px rgba(0, 149, 163, .25);
}

/* Hover effect for active items */
[data-active="true"]:hover {
    background: var(--menu-item-active-hover-bg) !important;
}

/* Non-active item hover */
[data-active="false"]:hover {
    background: var(--menu-item-hover-bg) !important;
}

/* Icon color enhancement for active state */
[data-active="true"] i {
    color: var(--menu-item-active-text-color) !important;
}

/* Text color enhancement for active state */
[data-active="true"] span {
    color: var(--menu-item-active-text-color) !important;
    font-weight: var(--menu-item-active-font-weight);
}
</style>
